import { type LoginInput, loginSchema } from "@edm/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "@tanstack/react-router";
import { Eye, EyeOff, LockKeyhole, ShieldCheck, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { BrandMark } from "@/components/brand-mark";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { APP_NAME, APP_VERSION, DESIGN_VERSION, SOP_VERSION } from "@/config/app";
import { ApiError } from "@/lib/api";
import { useLogin } from "@/lib/auth";

interface TurnstileApi {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "expired-callback"?: () => void;
    },
  ) => string;
  reset: (widgetId?: string) => void;
}

export function LoginPage() {
  const navigate = useNavigate();
  const login = useLogin();
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [turnstile, setTurnstile] = useState<{ enabled: boolean; siteKey: string | null }>({
    enabled: false,
    siteKey: null,
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<string | null>(null);
  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { identifier: "", password: "" },
  });

  useEffect(() => {
    let cancelled = false;
    fetch("/api/v1/auth/security-config")
      .then((response) => response.json())
      .then((body: { data?: { turnstileEnabled?: boolean; turnstileSiteKey?: string | null } }) => {
        if (cancelled) return;
        setTurnstile({
          enabled: Boolean(body.data?.turnstileEnabled),
          siteKey: body.data?.turnstileSiteKey ?? null,
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!turnstile.enabled || !turnstile.siteKey || !turnstileRef.current) return;
    const existing = document.querySelector<HTMLScriptElement>('script[data-edm-turnstile="1"]');
    const render = () => {
      const api = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
      if (!api || !turnstileRef.current) return;
      widgetRef.current = api.render(turnstileRef.current, {
        sitekey: turnstile.siteKey ?? "",
        callback: (token) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(""),
      });
    };
    if (existing) {
      render();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.edmTurnstile = "1";
    script.onload = render;
    document.head.appendChild(script);
  }, [turnstile.enabled, turnstile.siteKey]);

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      if (turnstile.enabled && !turnstileToken) {
        setError("请完成人机验证");
        return;
      }
      const me = await login.mutateAsync({ ...values, turnstileToken: turnstileToken || null });
      toast.success(`欢迎回来，${me.user.displayName}`);
      const pendingConfirm = sessionStorage.getItem("edm_confirm_token");
      if (pendingConfirm && !me.user.mustChangePassword) {
        sessionStorage.removeItem("edm_confirm_token");
        navigate({ to: "/confirm/$token", params: { token: pendingConfirm }, replace: true });
        return;
      }
      navigate({ to: me.user.mustChangePassword ? "/change-password" : "/", replace: true });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "登录失败，请稍后重试";
      setError(message);
    }
  });

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <section className="relative hidden overflow-hidden bg-sidebar p-10 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="absolute inset-0 grid-fade opacity-40" />
        <div className="absolute -right-24 top-24 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="absolute -bottom-24 left-10 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <BrandMark className="bg-white/10 text-blue-200" />
            <div>
              <p className="text-sm font-semibold">{APP_NAME}</p>
              <p className="text-xs text-slate-400">Enterprise Device Management</p>
            </div>
          </div>
          <div className="mt-20 max-w-xl space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-blue-200">
              <Sparkles className="h-3.5 w-3.5" />
              Cloudflare 免费层 · Web 全闭环
            </div>
            <h1 className="text-4xl font-semibold leading-tight tracking-tight">
              让每一台设备
              <br />
              都有清晰的责任与轨迹
            </h1>
            <p className="max-w-lg text-sm leading-6 text-slate-300">
              覆盖资产、低值易耗、耗材与软件许可四类物料，从入库、申领、审批、签收，
              到维修、归还、回收与处置，全程留痕。
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                { icon: ShieldCheck, title: "责任到人", text: "接收人确认后才生效" },
                { icon: LockKeyhole, title: "权限可控", text: "角色 + 数据范围" },
                { icon: Sparkles, title: "体验优先", text: "少点击、少输入" },
              ].map((item) => (
                <div key={item.title} className="rounded-xl border border-white/10 bg-white/5 p-4">
                  <item.icon className="h-4 w-4 text-blue-300" />
                  <p className="mt-3 text-sm font-medium">{item.title}</p>
                  <p className="mt-1 text-xs leading-5 text-slate-400">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="relative flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
          <span>版本 {APP_VERSION}</span>
          <span>SOP {SOP_VERSION}</span>
          <span>设计规格 {DESIGN_VERSION}</span>
        </div>
      </section>

      <section className="flex items-center justify-center bg-background px-4 py-10 sm:px-8">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <BrandMark />
            <div>
              <p className="text-sm font-semibold">{APP_NAME}</p>
              <p className="text-xs text-muted-foreground">Enterprise Device Management</p>
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
            <div className="space-y-2">
              <h2 className="text-2xl font-semibold tracking-tight">登录</h2>
              <p className="text-sm text-muted-foreground">使用手机号、邮箱或 ADID 登录系统。</p>
            </div>

            {error ? (
              <Alert variant="danger" className="mt-5" title="登录未完成">
                {error}
              </Alert>
            ) : null}

            <form className="mt-6 space-y-5" onSubmit={onSubmit} noValidate>
              <div className="space-y-2">
                <Label htmlFor="identifier">手机号 / 邮箱 / ADID</Label>
                <Input
                  id="identifier"
                  autoComplete="username"
                  placeholder="请输入登录标识"
                  aria-invalid={Boolean(form.formState.errors.identifier)}
                  {...form.register("identifier")}
                />
                {form.formState.errors.identifier ? (
                  <p className="text-xs text-red-600">{form.formState.errors.identifier.message}</p>
                ) : null}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">密码</Label>
                  <span className="text-xs text-muted-foreground">首次登录后需修改密码</span>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="请输入密码"
                    className="pr-10"
                    aria-invalid={Boolean(form.formState.errors.password)}
                    {...form.register("password")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? "隐藏密码" : "显示密码"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {form.formState.errors.password ? (
                  <p className="text-xs text-red-600">{form.formState.errors.password.message}</p>
                ) : null}
              </div>

              {turnstile.enabled ? (
                <div className="space-y-2">
                  <div ref={turnstileRef} />
                  <p className="text-xs text-muted-foreground">
                    请完成 Cloudflare Turnstile 人机验证。
                  </p>
                </div>
              ) : null}
              <Button type="submit" className="w-full" disabled={login.isPending}>
                {login.isPending ? "正在登录…" : "登录"}
              </Button>
            </form>

            <div className="mt-6 flex items-center justify-between text-xs text-muted-foreground">
              <span>首次部署？</span>
              <Link to="/setup" className="font-medium text-primary hover:underline">
                初始化系统管理员
              </Link>
            </div>
          </div>

          <p className="mt-6 text-center text-xs leading-5 text-muted-foreground">
            登录即表示你已阅读并同意管理单位的设备管理规程。
            <br />
            本系统为内部业务系统，请勿共享账号。
          </p>
        </div>
      </section>
    </div>
  );
}
